//src/pages/inventory/PurchaseInvoice/CreateNewLabTest.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  IconButton,
  Divider,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  LARGE_FORM_MODAL_SX,
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
  FORM_SECTION_TITLE_SX,
} from "../../../utils/formModalStyles";
import { createLabTestInBaseTable, updateLabTestInBaseTable } from "../../../services/labtestService";
import { getSampleCollections } from "../../../services/labService";
import DeleteIcon from '@mui/icons-material/Delete';
import axios from "axios";
import AddIcon from '@mui/icons-material/Add';
import { PREDEFINED_TEST_CATEGORIES } from "../../PathManagePatient/predefinedTestCategories";
import {
  getBomQtyUnit,
  parseDecimalNumber,
} from "../../../utils/pathItemPricingUtils";

const validationSchema = Yup.object({
  amount: Yup.number().required("Amount is required").min(0, "Amount must be at least 0"),
});

const isRequiredQtyMissing = (value) => {
  const qty = parseDecimalNumber(value, NaN);
  return !Number.isFinite(qty) || qty <= 0;
};

const validateSampleAndBomRequiredQuantities = (samples, boms) => {
  let hasErrors = false;
  const sampleErrors = [];
  const bomErrors = [];

  samples.forEach((entry, idx) => {
    const err = {};
    if (entry.sample_id && isRequiredQtyMissing(entry.requiredQuantity)) {
      err.requiredQuantity = "Required quantity is required";
      hasErrors = true;
    }
    sampleErrors[idx] = err;
  });

  boms.forEach((entry, idx) => {
    const err = {};
    if (entry.bom_id && isRequiredQtyMissing(entry.requiredQuantity)) {
      err.requiredQuantity = "Required quantity is required";
      hasErrors = true;
    }
    bomErrors[idx] = err;
  });

  return { hasErrors, sampleErrors, bomErrors };
};

const CreateNewLabTest = ({ open, onClose, product_info, createInBaseTable = false }) => {
  const [sampleCollections, setSampleCollections] = useState([]);
  const [boms, setBoms] = useState([]);
  const [sampleEntries, setSampleEntries] = useState([{ sample_id: "", requiredQuantity: "" }]);
  const [bomEntries, setBomEntries] = useState([{ bom_id: "", requiredQuantity: "" }]);
  const [sampleEntryErrors, setSampleEntryErrors] = useState([]);
  const [bomEntryErrors, setBomEntryErrors] = useState([]);
  const [subCategories, setSubCategories] = useState([{ name: "", referenceValue: "", unit: "" }]);
  const [selectedTestType, setSelectedTestType] = useState("");
  const [isCustomTest, setIsCustomTest] = useState(false);
  const [customTestName, setCustomTestName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: product_info?.name || "",
      amount: product_info?.amount || 0,
      sampleEntries: sampleEntries,
      bomEntries: bomEntries,
      subCategories: subCategories,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (isSubmitting) return; // Prevent multiple submissions
      
      try {
        setIsSubmitting(true);
        
        // Validate required fields
        if (!isCustomTest && !selectedTestType) {
          alert("Please select a test type or create a custom test.");
          return;
        }
        
        if (isCustomTest && !customTestName.trim()) {
          alert("Please enter a custom test name.");
          return;
        }
        
        if (!values.amount || values.amount <= 0) {
          alert("Please enter a valid amount.");
          return;
        }

        const qtyValidation = validateSampleAndBomRequiredQuantities(
          sampleEntries,
          bomEntries
        );
        if (qtyValidation.hasErrors) {
          setSampleEntryErrors(qtyValidation.sampleErrors);
          setBomEntryErrors(qtyValidation.bomErrors);
          alert("Please enter required quantity for all selected samples and consumables.");
          return;
        }
        setSampleEntryErrors([]);
        setBomEntryErrors([]);

        const linkedSampleEntries = sampleEntries
          .filter((entry) => entry.sample_id)
          .map((entry) => ({
            sample_id: entry.sample_id,
            requiredQuantity: parseDecimalNumber(entry.requiredQuantity, 0),
          }));

        const linkedBomEntries = bomEntries
          .filter((entry) => entry.bom_id)
          .map((entry) => ({
            bom_id: entry.bom_id,
            requiredQuantity: parseDecimalNumber(entry.requiredQuantity, 0),
          }));

        // Determine category: use selectedTestType for predefined tests, or test name for custom tests
        const category = selectedTestType || (isCustomTest ? customTestName : null);
        
        const req_data = {
          name: isCustomTest ? customTestName : selectedTestType,
          testType: selectedTestType || null, // Include test type
          category: category || null, // Include category field for lab_test table
          amount: values.amount,
          sampleId: linkedSampleEntries.map((entry) => entry.sample_id).join(","),
          sampleEntries: linkedSampleEntries,
          bomId: linkedBomEntries.map((entry) => entry.bom_id).join(","),
          bomEntries: linkedBomEntries,
          subCategories: subCategories.filter(sub => sub.name.trim() !== "").map(sub => ({
            name: sub.name || null,
            referenceValue: sub.referenceValue || null,
            unit: sub.unit || null,
            category: sub.category || null // Include category for Urine Test sub-categories
          })), // Include sub-categories with null handling
        };
   
        // console.log("Request Payload:", req_data); // Debugging line to check the payload
        
        let response;
        if (product_info?.id) {
          // For updates, use base table function (not organization-specific)
          response = await updateLabTestInBaseTable(product_info.id, req_data);
        } else {
          // For creates, always use base table (not organization-specific)
          response = await createLabTestInBaseTable(req_data);
        }
        
        // console.log("Response:", response); // Debug response
        
        // Show success message
        alert(product_info?.id ? "Lab test updated successfully!" : "Lab test created successfully!");
        
        handleClose(true); // Close and signal parent to refresh the table
      } catch (error) {
        console.error("Error in form submission:", error);
        alert(`Error: ${error.response?.data?.message || error.message || "Failed to save lab test"}`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const samples = await getSampleCollections();
        
        // Fetch BOMs from basic table (not organization-specific)
        let bomsData = [];
        try {
          console.log(`[CreateNewLabTest] Fetching BOMs from basic table (not organization-specific)...`);
          
          // Get backend URL - use same logic as apiClient
          const getEnvVar = (key, defaultValue = null) => {
            if (typeof window !== 'undefined' && window.env && typeof window.env === 'object' && window.env[key]) {
              return window.env[key];
            }
            if (process.env && process.env[key]) {
              return process.env[key];
            }
            return defaultValue;
          };
          
          const BACKEND_URL = getEnvVar('REACT_APP_BACKEND_URL', 'http://localhost:8080/api');
          const apiUrl = `${BACKEND_URL}/lab/boms`;
          
          // Get authentication token
          const token = localStorage.getItem("token");
          
          // Use axios directly to fetch from base table (without organizationId)
          // This ensures we fetch from the basic BOM table, not organization-specific table
          const response = await axios.get(apiUrl, {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          
          bomsData = Array.isArray(response.data) ? response.data : [];
          console.log(`[CreateNewLabTest] ✅ Fetched ${bomsData.length} BOMs from basic table`);
        } catch (error) {
          console.error(`[CreateNewLabTest] ❌ Error fetching BOMs:`, error);
          // Set empty array on error, don't block the rest of the form
          bomsData = [];
        }
        
        setSampleCollections(samples);
        setBoms(bomsData);
        // Prefill from product_info
        if (product_info) {
          // Prefill test type and name first.
          // Use saved name as source of truth so a custom test (e.g. "T3") is not
          // treated as its parent predefined profile (e.g. "Thyroid Profile").
          const testName = product_info.name || "";
          const testType = product_info.testType || null;
          const predefinedKeys = Object.keys(PREDEFINED_TEST_CATEGORIES);
          const nameIsPredefined = testName && predefinedKeys.includes(testName);
          const typeIsPredefined = testType && predefinedKeys.includes(testType);

          if (nameIsPredefined) {
            setSelectedTestType(testName);
            setIsCustomTest(false);
            setCustomTestName("");
          } else if (testName) {
            // Custom / standalone test — keep editing this test as-is
            setIsCustomTest(true);
            setCustomTestName(testName);
            setSelectedTestType("");
          } else if (typeIsPredefined) {
            setSelectedTestType(testType);
            setIsCustomTest(false);
            setCustomTestName("");
          } else {
            setIsCustomTest(false);
            setCustomTestName("");
            setSelectedTestType("");
          }
          
          // Prefill sample entries (with per-test required qty when saved)
          if (Array.isArray(product_info.sampleEntries) && product_info.sampleEntries.length) {
            setSampleEntries(
              product_info.sampleEntries.map((se) => {
                const sampleId = Number(se.sample_id ?? se.sampleId);
                let requiredQty =
                  se.requiredQuantity ?? se.required_quantity ?? se.requiredquantity ?? "";

                if (
                  (requiredQty === "" || requiredQty === null || requiredQty === undefined) &&
                  sampleId
                ) {
                  const sampleFromMaster = samples.find(
                    (s) => Number(s.id) === Number(sampleId)
                  );
                  if (sampleFromMaster?.requiredQuantity != null) {
                    requiredQty = sampleFromMaster.requiredQuantity;
                  }
                }

                return {
                  sample_id: sampleId,
                  requiredQuantity: requiredQty ?? "",
                };
              })
            );
          } else if (product_info.sampleId != null) {
            const parsedSamples = String(product_info.sampleId)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((id) => {
                const sampleIdNum = Number(id);
                const sampleFromMaster = samples.find(
                  (s) => Number(s.id) === sampleIdNum
                );
                return {
                  sample_id: sampleIdNum,
                  requiredQuantity: sampleFromMaster?.requiredQuantity ?? "",
                };
              });
            if (parsedSamples.length) setSampleEntries(parsedSamples);
          }
          
          // Prefill BOM entries with requiredQuantity
          if (Array.isArray(product_info.bomEntries) && product_info.bomEntries.length) {
            // Use bomEntries array directly (preferred method)
            const mappedBomEntries = product_info.bomEntries.map(be => {
              // Handle different field name variations
              const bomId = be.bom_id ?? be.bomId ?? be.id;
              let requiredQty = be.requiredQuantity ?? be.required_quantity ?? be.requiredquantity ?? "";

              // If requiredQuantity is missing on the link, fall back to BOM master default
              if ((requiredQty === "" || requiredQty === null || requiredQty === undefined) && bomId != null) {
                const bomFromMaster = bomsData.find(b => Number(b.id) === Number(bomId));
                if (bomFromMaster && bomFromMaster.requiredQuantity != null) {
                  requiredQty = bomFromMaster.requiredQuantity;
                }
              }

              return { 
                bom_id: Number(bomId), 
                requiredQuantity: requiredQty ?? "" 
              };
            });
            setBomEntries(mappedBomEntries);
          } else if (product_info.bomId != null) {
            // Parse bomId string and try to get requiredQuantity from bomEntries if available
            const bomIdString = String(product_info.bomId);
            const bomIds = bomIdString.split(',').map(s => s.trim()).filter(Boolean);
            
            const parsedBoms = bomIds.map((id) => {
              const bomIdNum = Number(id);
              // Try to find matching bomEntry to get requiredQuantity
              let requiredQty = "";
              
              // Check if bomEntries exists (even if empty, it might be an array)
              if (Array.isArray(product_info.bomEntries) && product_info.bomEntries.length > 0) {
                const matchingEntry = product_info.bomEntries.find(be => {
                  const entryBomId = Number(be.bom_id ?? be.bomId ?? be.id ?? 0);
                  return entryBomId === bomIdNum;
                });
                
                if (matchingEntry) {
                  requiredQty = matchingEntry.requiredQuantity ?? matchingEntry.required_quantity ?? matchingEntry.requiredquantity ?? "";
                }
              }

              // If still missing, fall back to BOM master default
              if ((requiredQty === "" || requiredQty === null || requiredQty === undefined)) {
                const bomFromMaster = bomsData.find(b => Number(b.id) === bomIdNum);
                if (bomFromMaster && bomFromMaster.requiredQuantity != null) {
                  requiredQty = bomFromMaster.requiredQuantity;
                }
              }
              
              return { 
                bom_id: bomIdNum,
                requiredQuantity: requiredQty ?? ""
              };
            });
            
            if (parsedBoms.length > 0) {
              setBomEntries(parsedBoms);
            }
          }
          
          // Prefill sub-categories from the saved test only.
          // Do not infer a predefined parent profile from subcategory names
          // (e.g. custom "T3" must not become "Thyroid Profile").
          const determinedTestType = nameIsPredefined
            ? testName
            : (!testName && typeIsPredefined ? testType : null);

          if (Array.isArray(product_info.subCategories) && product_info.subCategories.length) {
            // Use sub-categories from product_info (these may be edited versions of predefined or custom test sub-categories)
            // Format sub-categories to ensure they have the correct structure
            const formattedSubCategories = product_info.subCategories.map(sub => ({
              name: sub.name || "",
              referenceValue: sub.referenceValue || sub.reference_value || "",
              unit: sub.unit || "",
              category: sub.category || null
            }));
            setSubCategories(formattedSubCategories);
          } else {
            // If no sub-categories in product_info, use predefined if it's a predefined test
            if (determinedTestType && PREDEFINED_TEST_CATEGORIES[determinedTestType]) {
              setSubCategories(PREDEFINED_TEST_CATEGORIES[determinedTestType]);
            } else {
              // For custom tests or tests without predefined sub-categories, start with empty array
              // Don't reset to single empty entry if we're editing a custom test (let user see existing empty state)
              setSubCategories([{ name: "", referenceValue: "", unit: "" }]);
            }
          }
          
          // Update formik values
          formik.setFieldValue('name', testName, false);
          formik.setFieldValue('amount', product_info.amount || 0, false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [product_info]);

  // Keep formik synced when we compute entries after open
  useEffect(() => {
    formik.setFieldValue('sampleEntries', sampleEntries, false);
  }, [sampleEntries]);
  useEffect(() => {
    formik.setFieldValue('bomEntries', bomEntries, false);
  }, [bomEntries]);
  useEffect(() => {
    formik.setFieldValue('subCategories', subCategories, false);
  }, [subCategories]);

  const handleAddSampleEntry = () => {
    setSampleEntries([...sampleEntries, { sample_id: "", requiredQuantity: "" }]);
  };

  const handleAddBOMEntry = () => {
    setBomEntries([...bomEntries, { bom_id: "", requiredQuantity: "" }]);
  };

  const handleAddSubCategory = () => {
    setSubCategories([...subCategories, { name: "", referenceValue: "", unit: "" }]);
  };

  const handleTestTypeChange = (testType) => {
    const nextType = testType || "";
    setSelectedTestType(nextType);
    setIsCustomTest(false);
    setCustomTestName("");
    if (nextType && PREDEFINED_TEST_CATEGORIES[nextType]) {
      setSubCategories(PREDEFINED_TEST_CATEGORIES[nextType]);
    } else {
      setSubCategories([{ name: "", referenceValue: "", unit: "" }]);
    }
  };

  const handleCustomTestToggle = () => {
    setIsCustomTest(!isCustomTest);
    if (!isCustomTest) {
      setSelectedTestType("");
      setSubCategories([{ name: "", referenceValue: "", unit: "" }]);
    }
  };

  const handleSampleChange = (index, value) => {
    const newEntries = [...sampleEntries];
    newEntries[index].sample_id = value ? value.id : "";
    newEntries[index].requiredQuantity = value
      ? value.requiredQuantity ?? value.required_quantity ?? ""
      : "";
    setSampleEntries(newEntries);

    const newErrors = [...sampleEntryErrors];
    if (newErrors[index]?.requiredQuantity) {
      newErrors[index] = { ...newErrors[index], requiredQuantity: undefined };
      setSampleEntryErrors(newErrors);
    }
  };

  const handleSampleRequiredQuantityChange = (index, value) => {
    const newEntries = [...sampleEntries];
    newEntries[index].requiredQuantity = value;
    setSampleEntries(newEntries);

    const newErrors = [...sampleEntryErrors];
    if (!newErrors[index]) newErrors[index] = {};
    if (isRequiredQtyMissing(value)) {
      newErrors[index].requiredQuantity = "Required quantity is required";
    } else {
      delete newErrors[index].requiredQuantity;
    }
    setSampleEntryErrors(newErrors);
  };

  const handleBOMChange = (index, value) => {
    const newEntries = [...bomEntries];
    newEntries[index].bom_id = value ? value.id : "";
    newEntries[index].requiredQuantity = value
      ? value.requiredQuantity ?? value.required_quantity ?? ""
      : "";
    setBomEntries(newEntries);

    const newErrors = [...bomEntryErrors];
    if (newErrors[index]?.requiredQuantity) {
      newErrors[index] = { ...newErrors[index], requiredQuantity: undefined };
      setBomEntryErrors(newErrors);
    }
  };

  const handleBomRequiredQuantityChange = (index, value) => {
    const newEntries = [...bomEntries];
    newEntries[index].requiredQuantity = value;
    setBomEntries(newEntries);

    const newErrors = [...bomEntryErrors];
    if (!newErrors[index]) newErrors[index] = {};
    if (isRequiredQtyMissing(value)) {
      newErrors[index].requiredQuantity = "Required quantity is required";
    } else {
      delete newErrors[index].requiredQuantity;
    }
    setBomEntryErrors(newErrors);
  };

  const removeSampleEntry = (index) => {
    const newEntries = [...sampleEntries];
    newEntries.splice(index, 1);
    setSampleEntries(newEntries);
  };

  const removeBOMEntry = (index) => {
    const newEntries = [...bomEntries];
    newEntries.splice(index, 1);
    setBomEntries(newEntries);
  };

  const handleSubCategoryChange = (index, field, value) => {
    const newEntries = [...subCategories];
    newEntries[index][field] = value;
    setSubCategories(newEntries);
  };

  const removeSubCategory = (index) => {
    const newEntries = [...subCategories];
    newEntries.splice(index, 1);
    setSubCategories(newEntries);
  };

  const handleClose = (didChange = false) => {
    const shouldRefresh = didChange === true;
    formik.resetForm(); // Reset form values
    setSampleEntries([{ sample_id: "", requiredQuantity: "" }]); // Reset sample entries
    setBomEntries([{ bom_id: "", requiredQuantity: "" }]); // Reset BOM entries
    setSampleEntryErrors([]);
    setBomEntryErrors([]);
    setSubCategories([{ name: "", referenceValue: "", unit: "" }]); // Reset sub-categories
    setSelectedTestType(""); // Reset test type
    setIsCustomTest(false); // Reset custom test flag
    setCustomTestName(""); // Reset custom test name
    setIsSubmitting(false); // Reset submitting state
    onClose?.(shouldRefresh); // Parent refreshes table only after successful save
  };

  return (
    <BasicModal
      open={open}
      onClose={handleClose}
      title={product_info?.id ? "Edit Lab Test" : "Create New Lab Test"}
      sx={LARGE_FORM_MODAL_SX}
    >
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
        <form
          onSubmit={(e) => {
            formik.handleSubmit(e);
          }}
        >
          <Grid container spacing={1}>
            {/* Test Type Selection */}
            <Grid item xs={12}>
              <Typography sx={FORM_SECTION_TITLE_SX}>Test Selection</Typography>
            </Grid>

            {!isCustomTest ? (
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={["", ...Object.keys(PREDEFINED_TEST_CATEGORIES)]}
                  value={selectedTestType || ""}
                  onChange={(event, value) => handleTestTypeChange(value)}
                  getOptionLabel={(option) => (option ? option : "None")}
                  isOptionEqualToValue={(option, value) => (option || "") === (value || "")}
                  renderOption={(props, option) => (
                    <li {...props} key={option || "__none__"}>
                      {option ? option : "None"}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Test Type"
                      placeholder="Choose from predefined tests (CBC, LFT, KFT, etc.)"
                      {...DENSE_FIELD_PROPS}
                    />
                  )}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Custom Test Name"
                  value={customTestName}
                  onChange={(e) => setCustomTestName(e.target.value)}
                  placeholder="Enter custom test name"
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                size="small"
                variant={isCustomTest ? "outlined" : "contained"}
                onClick={handleCustomTestToggle}
                sx={{ height: 32, textTransform: "none", fontWeight: 600 }}
              >
                {isCustomTest ? "Use Predefined" : "Create Custom Test"}
              </Button>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                id="amount"
                name="amount"
                label="Amount"
                type="number"
                value={formik.values.amount}
                onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            {/* Sub-Categories Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={{ ...FORM_SECTION_TITLE_SX, mt: 0.5 }}>
                Test Sub-Categories
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.75, fontSize: "0.75rem", lineHeight: 1.3 }}
              >
                {selectedTestType
                  ? `Sub-categories for ${selectedTestType} test. You can edit the reference values and units.`
                  : isCustomTest
                    ? "Add sub-categories for your custom test."
                    : "Select a test type above to see predefined sub-categories."}
              </Typography>
              {(() => {
                if (selectedTestType === "Urine Test") {
                  const groupedCategories = subCategories.reduce((acc, subCategory, index) => {
                    const category = subCategory.category || "Other";
                    if (!acc[category]) {
                      acc[category] = [];
                    }
                    acc[category].push({ ...subCategory, originalIndex: index });
                    return acc;
                  }, {});

                  return Object.entries(groupedCategories).map(([categoryName, items]) => (
                    <Box key={categoryName} sx={{ mb: 1.25 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ mb: 0.5, color: "primary.main", fontSize: "0.85rem" }}
                      >
                        {categoryName} Section
                      </Typography>
                      {items.map((subCategory, itemIndex) => (
                        <Grid
                          container
                          spacing={1}
                          key={`${categoryName}-${itemIndex}`}
                          alignItems="center"
                          sx={{ mb: 0.75 }}
                        >
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Sub-Category Name"
                              value={subCategory.name}
                              onChange={(e) =>
                                handleSubCategoryChange(
                                  subCategory.originalIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., Hemoglobin, TLC, DLC"
                              sx={{
                                "& .MuiInputBase-input": {
                                  fontWeight:
                                    categoryName === subCategory.name ? "bold" : "normal",
                                },
                              }}
                              {...DENSE_FIELD_PROPS}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Reference Value"
                              value={subCategory.referenceValue}
                              onChange={(e) =>
                                handleSubCategoryChange(
                                  subCategory.originalIndex,
                                  "referenceValue",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., M-14-18gm%, F-12-16gm%"
                              {...DENSE_FIELD_PROPS}
                            />
                          </Grid>
                          <Grid item xs={10} sm={3}>
                            <TextField
                              fullWidth
                              label="Unit"
                              value={subCategory.unit}
                              onChange={(e) =>
                                handleSubCategoryChange(
                                  subCategory.originalIndex,
                                  "unit",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., Gm/dl, %, /cumm"
                              {...DENSE_FIELD_PROPS}
                            />
                          </Grid>
                          <Grid item xs={2} sm={1}>
                            <IconButton
                              size="small"
                              onClick={() => removeSubCategory(subCategory.originalIndex)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  ));
                }

                return subCategories.map((subCategory, index) => (
                  <Grid
                    container
                    spacing={1}
                    key={index}
                    alignItems="center"
                    sx={{ mb: 0.75 }}
                  >
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Sub-Category Name"
                        value={subCategory.name}
                        onChange={(e) =>
                          handleSubCategoryChange(index, "name", e.target.value)
                        }
                        placeholder="e.g., Hemoglobin, TLC, DLC"
                        {...DENSE_FIELD_PROPS}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Reference Value"
                        value={subCategory.referenceValue}
                        onChange={(e) =>
                          handleSubCategoryChange(index, "referenceValue", e.target.value)
                        }
                        placeholder="e.g., M-14-18gm%, F-12-16gm%"
                        {...DENSE_FIELD_PROPS}
                      />
                    </Grid>
                    <Grid item xs={10} sm={3}>
                      <TextField
                        fullWidth
                        label="Unit"
                        value={subCategory.unit}
                        onChange={(e) =>
                          handleSubCategoryChange(index, "unit", e.target.value)
                        }
                        placeholder="e.g., Gm/dl, %, /cumm"
                        {...DENSE_FIELD_PROPS}
                      />
                    </Grid>
                    <Grid item xs={2} sm={1}>
                      <IconButton size="small" onClick={() => removeSubCategory(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ));
              })()}
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleAddSubCategory}
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                sx={{ textTransform: "none" }}
              >
                Add Sub-Category
              </Button>
            </Grid>

            {/* Sample Collection Entries */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={{ ...FORM_SECTION_TITLE_SX, mt: 0.5 }}>
                Sample Collections
              </Typography>
              {sampleEntries.map((entry, index) => (
                <Grid
                  container
                  spacing={1}
                  key={index}
                  alignItems="center"
                  sx={{ mb: 0.75 }}
                >
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      size="small"
                      options={sampleCollections}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === (value?.id ?? value)
                      }
                      value={
                        sampleCollections.find((s) => s.id === entry.sample_id) || null
                      }
                      onChange={(event, value) => handleSampleChange(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sample Collection"
                          error={
                            formik.touched.sampleEntries &&
                            Boolean(formik.errors.sampleEntries)
                          }
                          helperText={
                            formik.touched.sampleEntries && formik.errors.sampleEntries
                          }
                          {...DENSE_FIELD_PROPS}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    {entry.sample_id && (
                      <TextField
                        fullWidth
                        label="Unit Type"
                        value={
                          sampleCollections.find((sample) => sample.id === entry.sample_id)
                            ?.unitType || ""
                        }
                        disabled
                        {...DENSE_FIELD_PROPS}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    {entry.sample_id && (
                      <TextField
                        fullWidth
                        type="number"
                        label="Required Quantity"
                        value={entry.requiredQuantity ?? ""}
                        onChange={(e) =>
                          handleSampleRequiredQuantityChange(index, e.target.value)
                        }
                        error={Boolean(sampleEntryErrors[index]?.requiredQuantity)}
                        helperText={sampleEntryErrors[index]?.requiredQuantity}
                        inputProps={{ min: 0, step: "any" }}
                        required
                        {...DENSE_FIELD_PROPS}
                      />
                    )}
                  </Grid>
                  <Grid item xs={2} sm={1}>
                    <IconButton size="small" onClick={() => removeSampleEntry(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleAddSampleEntry}
                variant="outlined"
                size="small"
                sx={{ textTransform: "none" }}
              >
                Add More
              </Button>
            </Grid>

            {/* BOM Entries */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={{ ...FORM_SECTION_TITLE_SX, mt: 0.5 }}>
                Consumables
              </Typography>
              {bomEntries.map((entry, index) => (
                <Grid
                  container
                  spacing={1}
                  key={index}
                  alignItems="center"
                  sx={{ mb: 0.75 }}
                >
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      size="small"
                      options={boms}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === (value?.id ?? value)
                      }
                      value={boms.find((b) => b.id === entry.bom_id) || null}
                      onChange={(event, value) => handleBOMChange(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Consumable"
                          error={
                            formik.touched.bomEntries && Boolean(formik.errors.bomEntries)
                          }
                          helperText={
                            formik.touched.bomEntries && formik.errors.bomEntries
                          }
                          {...DENSE_FIELD_PROPS}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    {entry.bom_id && (
                      <TextField
                        fullWidth
                        label="Qty Unit"
                        value={
                          getBomQtyUnit(
                            boms.find((bom) => bom.id === entry.bom_id)
                          ) || ""
                        }
                        disabled
                        {...DENSE_FIELD_PROPS}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    {entry.bom_id && (
                      <TextField
                        fullWidth
                        type="number"
                        label="Required Quantity"
                        value={entry.requiredQuantity ?? ""}
                        onChange={(e) =>
                          handleBomRequiredQuantityChange(index, e.target.value)
                        }
                        error={Boolean(bomEntryErrors[index]?.requiredQuantity)}
                        helperText={bomEntryErrors[index]?.requiredQuantity}
                        inputProps={{ min: 0, step: "any" }}
                        required
                        {...DENSE_FIELD_PROPS}
                      />
                    )}
                  </Grid>
                  <Grid item xs={2} sm={1}>
                    <IconButton size="small" onClick={() => removeBOMEntry(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleAddBOMEntry}
                variant="outlined"
                size="small"
                sx={{ textTransform: "none" }}
              >
                Add More
              </Button>
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={handleClose}
                submitType="submit"
                submitLabel={product_info?.id ? "Update" : "Create"}
                submitDisabled={isSubmitting}
                loading={isSubmitting}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default CreateNewLabTest;
